import { CommonModule } from '@angular/common';
import { APP_INITIALIZER, Inject, InjectionToken, ModuleWithProviders, NgModule, Optional, SkipSelf } from '@angular/core';
import { ServiceWorkerModule, SwRegistrationOptions } from '@angular/service-worker';
import { PWA_SERVICE } from '@df/core/integration/integration-pwa.interface';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { DeepPartial } from '@df/util/type/deep-partial';
import { NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import {
    ICmsContentComponentValidationService,
    IValidtionEvent
} from '@icms/core/content/component/icms-content-component-validation.service';
import { ICmsContentService } from '@icms/core/content/icms-content.service';
import defaultsDeep from 'lodash-es/defaultsDeep';
import findIndex from 'lodash-es/findIndex';
import { PwaA2hsService } from './a2hs/pwa-a2hs.service';
import { PwaIntegrationService } from './pwa-integration.service';
import { NPwa } from './pwa.interface';
import { PWA_OPTIONS } from './pwa.options';
import { PwaService } from './pwa.service';
import { PwaUpdateService } from './update/pwa-update.service';

export const PWA_ROOT_GUARD = new InjectionToken<PwaService>('Internal PWA ForRoot Guard');

export function createPwaRootGuard(pwa: PwaService): 'guarded' | TypeError {
    if (pwa) {
        throw new TypeError('PwaModule.forRoot() called twice. Feature modules should use PwaModule.forFeature() instead.');
    }

    return 'guarded';
}

interface IComponentDataWithStyles extends NICmsContentComponent.IDataWithStyles {
    pwaVisibility: NPwa.EPwaVisibility;
}

@NgModule({
    imports: [CommonModule, TranslationModule, ServiceWorkerModule.register('sw.js')],
    providers: [
        {
            provide: PWA_OPTIONS,
            useFactory: () => {
                return NPwa.defaultOptions;
            }
        },
        { provide: PWA_SERVICE, useExisting: PwaIntegrationService }
    ]
})
export class PwaModule {
    constructor(pwaService: PwaService, @Optional() @Inject(PWA_ROOT_GUARD) guard: 'guarded' | TypeError) {
        if (guard === 'guarded') {
            pwaService.init();
        }
    }

    static forRoot(options?: DeepPartial<NPwa.IModuleOptions>): ModuleWithProviders<PwaModule> {
        return {
            ngModule: PwaModule,
            providers: [
                PwaA2hsService,
                PwaUpdateService,
                {
                    provide: PWA_OPTIONS,
                    useFactory: () => {
                        return defaultsDeep(options || {}, NPwa.defaultOptions);
                    }
                },
                {
                    provide: SwRegistrationOptions,
                    useFactory: (options: NPwa.IModuleOptions) => options,
                    deps: [PWA_OPTIONS]
                },
                {
                    provide: PWA_ROOT_GUARD,
                    useFactory: createPwaRootGuard,
                    deps: [[PwaService, new Optional(), new SkipSelf()]]
                },

                {
                    provide: APP_INITIALIZER,
                    useFactory:
                        (
                            iCmsContentComponentValidationService: ICmsContentComponentValidationService,
                            pwaIntegrationService: PwaIntegrationService
                        ) =>
                        () => {
                            ICmsContentService.registerComponentMetaPatch((meta: NICmsContentComponent.IMeta) => {
                                const hideFieldIndex = findIndex(meta.fields, field => field.key === 'styles.hidden');
                                if (hideFieldIndex > -1) {
                                    const hiddenField = meta.fields[hideFieldIndex] as NICmsContentField.IToggleField;
                                    hiddenField.title = 'Visible on Web and PWA:';
                                    hiddenField.invert = true;

                                    meta.fields.splice(hideFieldIndex + 1, 0, <NICmsContentField.IChoiceField>{
                                        key: 'styles.pwaVisibility',
                                        type: NICmsContentField.EFieldType.choice,
                                        group: NICmsContentComponent.EFieldGroup.styles,
                                        title: 'Visibility',
                                        default: NPwa.EPwaVisibility.none,
                                        options: [
                                            { key: NPwa.EPwaVisibility.web, title: 'Web only' },
                                            { key: NPwa.EPwaVisibility.pwa, title: 'PWA only' },
                                            { key: NPwa.EPwaVisibility.none, title: 'None' }
                                        ],
                                        canShow: i => !i?.styles?.hidden
                                    });
                                }
                            });

                            iCmsContentComponentValidationService.validation$.subscribe((event: IValidtionEvent) => {
                                const data = event.data as NICmsContentComponent.IDataWithStyles;
                                const pwaVisibility = (data?.styles as IComponentDataWithStyles)?.pwaVisibility;

                                if (
                                    (pwaVisibility === NPwa.EPwaVisibility.web && pwaIntegrationService.isStandalone) ||
                                    (pwaVisibility === NPwa.EPwaVisibility.pwa && !pwaIntegrationService.isStandalone)
                                ) {
                                    event.error = {
                                        copy: 'Not allowed for' + (pwaVisibility === NPwa.EPwaVisibility.web ? ' PWA' : ' web'),
                                        level: 'error'
                                    };
                                }
                            });
                        },
                    deps: [ICmsContentComponentValidationService, PwaIntegrationService],
                    multi: true
                }
            ]
        };
    }
}
